import { useState } from 'react'

import { type Timestamp, timestampDate } from '@bufbuild/protobuf/wkt'
import { Code, ConnectError } from '@connectrpc/connect'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'

import {
  Alert,
  Flex,
  HStack,
  PrimaryButton,
  TertiaryButton,
  Text,
} from '@enechain/ecloud-designsystem'
import { FaPenToSquareRegular } from '@enechain/ecloud-icons'

import { AuditPanel } from '~/components/common/AuditPanel'
import { CompactAlert } from '~/components/common/CompactAlert'
import { PageBody } from '~/components/common/PageBody'
import { PageLayout } from '~/components/common/PageLayout'
import { PageScrollArea } from '~/components/common/PageScrollArea'
import { PageSection } from '~/components/common/PageSection'
import { RegionLoading } from '~/components/common/RegionLoading'
import { UnsavedChangesDialog } from '~/components/common/UnsavedChangesDialog'
import { iconSize } from '~/constants/iconSize'
import { useCrudModalForm } from '~/hooks/useCrudModalForm'
import {
  convertListPlantMastersResponse,
  useListPlantMasters,
} from '~/hooks/useListPlantMasters'
import { useRevealOnChange } from '~/hooks/useRevealOnChange'
import { useUnsavedGuard } from '~/hooks/useUnsavedGuard'
import { UnitBasicView } from '~/pages/AdminPlantUnitDetail/components/UnitBasicView'
import { useGetPlantUnit } from '~/pages/AdminPlantUnitMaster/hooks/useGetPlantUnit'
import { useListGenerationSites } from '~/pages/AdminPlantUnitMaster/hooks/useListGenerationSites'
import { useUpdatePlantUnit } from '~/pages/AdminPlantUnitMaster/hooks/useUpdatePlantUnit'
import { convertListGenerationSitesResponse } from '~/pages/AdminPlantUnitMaster/models/siteRows'
import { shouldClearPlantOnSiteChange } from '~/pages/AdminPlantUnitMaster/models/unitCandidates'
import {
  type PlantUnitFormInput,
  type PlantUnitFormValues,
  buildPlantUnitFormSchema,
  defaultPlantUnitFormInput,
  plantUnitAttributesFromValues,
  plantUnitRowToFormInput,
} from '~/pages/AdminPlantUnitMaster/models/unitForm'
import { convertPlantUnit } from '~/pages/AdminPlantUnitMaster/models/unitRows'
import {
  PLANT_UNIT_ERROR_KEY_PREFIX,
  PLANT_UNIT_SAVE_ERROR_KEYS,
  PLANT_UNIT_UNKNOWN_ERROR_KEY,
  resolvePlantUnitSaveError,
} from '~/pages/AdminPlantUnitMaster/models/unitServerError'
import { showSiteAreaMismatchToast } from '~/pages/AdminPlantUnitMaster/models/unitToast'
import { routePaths } from '~/routes/routePaths'

/**
 * 電源ユニットの詳細 (`/admin/plant-unit-master/:id`)。基本情報を閲覧し、「編集」→
 * 「キャンセル」/「保存」で更新する。所属 BG と作成・更新の記録は読み取り専用。
 *
 * 保存の楽観ロックは、編集を始めて form を埋めた時点の更新時刻で行う。編集中に他の利用者が
 * 更新して再取得が走っても、form はその時点の値のままなので最新の時刻で送ると他の利用者の
 * 更新を上書きしてしまう。未保存のまま画面を離れる時は確認を出す。
 */
export const AdminPlantUnitDetailPage = (): React.ReactElement => {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const { data, isPending, isError, error } = useGetPlantUnit(id)
  const sitesQuery = useListGenerationSites()
  const plantsQuery = useListPlantMasters()
  const { mutateAsync, isPending: isSaving } = useUpdatePlantUnit()

  const unit =
    data?.plantUnit !== undefined ? convertPlantUnit(data.plantUnit) : undefined
  const siteRows =
    sitesQuery.data !== undefined
      ? convertListGenerationSitesResponse(sitesQuery.data)
      : []
  const plantRows =
    plantsQuery.data !== undefined
      ? convertListPlantMastersResponse(plantsQuery.data)
      : []

  const [isEditing, setEditing] = useState(false)
  // 編集を始めて form を埋めた時点の更新時刻 (保存の expectedUpdatedAt に使う)
  const [baseUpdatedAt, setBaseUpdatedAt] = useState<Timestamp | undefined>()

  const {
    control,
    errors,
    serverError,
    handleClose,
    handleSave,
    reset,
    setValue,
    getValues,
    isDirty,
  } = useCrudModalForm<PlantUnitFormInput, PlantUnitFormValues>({
    schema: buildPlantUnitFormSchema(t),
    defaultValues: defaultPlantUnitFormInput(),
    onSubmit: async (values) => {
      if (unit === undefined || baseUpdatedAt === undefined) {
        // 取得できていない間は編集を始められないので通常は到達しない
        throw new Error(t(PLANT_UNIT_UNKNOWN_ERROR_KEY))
      }
      const response = await mutateAsync({
        ...plantUnitAttributesFromValues(values),
        id: unit.id,
        generationSiteId: values.siteId,
        // proto Timestamp をそのまま渡す。JS Date を経由するとマイクロ秒が落ち、
        // DB の updated_at との比較が一致せず Aborted になる。
        expectedUpdatedAt: baseUpdatedAt,
      })
      showSiteAreaMismatchToast(t, response.siteAreaMismatch)
    },
    onClose: () => setEditing(false),
    customErrorKeys: PLANT_UNIT_SAVE_ERROR_KEYS,
    unknownErrorKey: PLANT_UNIT_UNKNOWN_ERROR_KEY,
    resolveDetailedError: (err, tt, values) =>
      resolvePlantUnitSaveError(err, tt, {
        unitName: values.unitName,
        siteId: values.siteId,
        siteName:
          siteRows.find((site) => site.id === values.siteId)?.siteName ?? '',
      }),
  })

  // 欄に付かないエラー (上部の Alert) は、下までスクロールして保存した時も見える位置へ寄せる
  const alertRef = useRevealOnChange<HTMLDivElement>(serverError)
  // 発電所を変えたら、新しい発電所のエリアに合わない系統コードの選択を外す
  const handleSiteChanged = (siteId: string | undefined): void => {
    const { plantMasterId } = getValues()
    if (plantMasterId === '') return
    if (
      shouldClearPlantOnSiteChange(
        siteRows.find((site) => site.id === siteId)?.areaCode,
        plantRows.find((row) => row.id === plantMasterId)?.areaCode,
      )
    ) {
      setValue('plantMasterId', '', { shouldDirty: true })
    }
  }

  const guard = useUnsavedGuard({ isDirty: isEditing && isDirty })

  const startEdit = (): void => {
    if (isSaving || unit === undefined) return
    reset(plantUnitRowToFormInput(unit))
    setBaseUpdatedAt(unit.updatedAt)
    setEditing(true)
  }

  const cancelEdit = (): void => {
    if (isSaving) return
    handleClose()
  }

  const siteName = siteRows.find(
    (site) => site.id === unit?.generationSiteId,
  )?.siteName
  const plant = plantRows.find((row) => row.id === unit?.plantMasterId)
  const plantLabel =
    plant === undefined ? undefined : `${plant.plantCode} ${plant.plantName}`

  const breadcrumbItems = [
    {
      label: t('page:adminPlantUnitMaster.breadcrumb.admin'),
      to: routePaths.adminHub.path,
    },
    {
      label: t('common:route.adminPlantUnitMaster'),
      to: routePaths.adminPlantUnitMaster.path,
    },
    { label: t('common:route.adminPlantUnitDetail'), bold: true },
  ]

  const loadErrorMessage = isError
    ? error instanceof ConnectError && error.code === Code.NotFound
      ? t('page:adminPlantUnitMaster.unit.detail.notFound')
      : t(`${PLANT_UNIT_ERROR_KEY_PREFIX}.loadFailed`)
    : undefined

  return (
    <PageLayout breadcrumbItems={breadcrumbItems}>
      <PageBody>
        {loadErrorMessage !== undefined ? (
          <Alert description={loadErrorMessage} status="error" />
        ) : isPending || unit === undefined ? (
          <RegionLoading minHeight="120px" isLoading />
        ) : (
          <PageScrollArea>
            <Flex flexDirection="column" gap={6}>
              <PageSection
                actions={
                  isEditing ? (
                    <HStack gap={2}>
                      <TertiaryButton
                        disabled={isSaving}
                        onClick={cancelEdit}
                        size="sm"
                      >
                        {t('common:action.cancel')}
                      </TertiaryButton>
                      <PrimaryButton
                        disabled={isSaving}
                        loading={isSaving}
                        onClick={handleSave}
                        size="sm"
                      >
                        {t('common:action.save')}
                      </PrimaryButton>
                    </HStack>
                  ) : (
                    <TertiaryButton onClick={startEdit} size="sm">
                      <FaPenToSquareRegular
                        boxSize={iconSize.labeledButton}
                        aria-hidden="true"
                      />
                      {t('common:action.edit')}
                    </TertiaryButton>
                  )
                }
                title={t('page:adminPlantUnitMaster.unit.detail.basic')}
              >
                <Flex flexDirection="column" gap={3}>
                  {isEditing && serverError !== undefined && (
                    <div ref={alertRef}>
                      <CompactAlert
                        role="alert"
                        status="error"
                        title={serverError}
                      />
                    </div>
                  )}
                  <UnitBasicView
                    editing={
                      isEditing
                        ? {
                            control,
                            errors,
                            disabled: isSaving,
                            sites: siteRows,
                            plants: plantRows,
                            onSiteChanged: handleSiteChanged,
                          }
                        : undefined
                    }
                    plantLabel={plantLabel}
                    siteName={siteName}
                    unit={unit}
                  />
                  {isEditing && isDirty && (
                    <Text textStyle="caption" color="Text.midEmphasis">
                      {t('page:adminPlantUnitMaster.unit.detail.unsavedNote')}
                    </Text>
                  )}
                  <AuditPanel
                    createdAt={
                      unit.createdAt === undefined
                        ? undefined
                        : timestampDate(unit.createdAt)
                    }
                    createdBy={unit.createdBy}
                    updatedAt={
                      unit.updatedAt === undefined
                        ? undefined
                        : timestampDate(unit.updatedAt)
                    }
                    updatedBy={unit.updatedBy}
                  />
                </Flex>
              </PageSection>
            </Flex>
          </PageScrollArea>
        )}
      </PageBody>
      <UnsavedChangesDialog
        isOpen={guard.isBlocked}
        onCancel={guard.cancel}
        onConfirm={guard.proceed}
      />
    </PageLayout>
  )
}
